import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {MonoText} from '@/components/shared/MonoText'
import {InfoBanner} from '@/components/shared/InfoBanner'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {Strong} from '@/components/shared/Strong'
import {useAuth} from '@/contexts/auth-context'
import {useServices} from '@/services/context'
import {permissions} from '@/lib/permissions'
import {routes} from '@/lib/routes'
import {USE_CASE_REGISTRY} from '@/types/form-layout'
import type {FormLayoutListItem} from '@/types/form-layout'
import {Card} from '@/components/ui/card'
import {Badge} from '@/components/ui/badge'
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table'
import {Skeleton} from '@/components/ui/skeleton'
import {PageLayout} from '@/components/shared/PageLayout'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {MutedText} from '@/components/shared/MutedText'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {FormLayoutControllerLayoutsEndpoint} from '@/types/endpoint-contracts'
import {CONCEPT_ICON} from '@/lib/concepts'

export function FormLayoutListPage() {
    const i18n = useI18n()
    const {hasPermission} = useAuth()
    const services = useServices()

    usePageTitle(i18n.formLayouts.pageTitle)

    const canManage = hasPermission(permissions.formLayouts.manage)

    const {
        queryString,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: FormLayoutControllerLayoutsEndpoint,
        sortable: true,
        defaultSortBy: FormLayoutControllerLayoutsEndpoint.sort.USE_CASE_KEY,
        defaultSortDir: SortDir.ASC,
    })

    const {data: layoutsPage, isLoading, error} = useQuery({
        queryKey: ['form-layouts', queryString],
        queryFn: () => services.formLayouts.list({
            ...Object.fromEntries(new URLSearchParams(queryString)),
            size: 1000,
        }),
        enabled: canManage,
    })

    const layouts: FormLayoutListItem[] = useMemo(
        () => layoutsPage?.content ?? [],
        [layoutsPage],
    )

    const visibleRows = useMemo(() => {
        const layoutsByKey = new Map(layouts.map((l) => [l.useCaseKey, l]))
        const allRows = USE_CASE_REGISTRY.map((useCase) => ({
            ...useCase,
            layout: layoutsByKey.get(useCase.useCaseKey) ?? null,
        }))
        const q = searchAndFilters.q?.toLowerCase() ?? ''
        const activeFilter = searchAndFilters.filters[FormLayoutControllerLayoutsEndpoint.filter.ACTIVE.name]
        return allRows.filter(row => {
            if (q) {
                const haystack = `${row.useCaseKey} ${row.displayName}`.toLowerCase()
                if (!haystack.includes(q)) return false
            }
            if (activeFilter !== undefined && activeFilter !== '') {
                const want = activeFilter === 'true'
                const isActive = row.layout?.active ?? false
                if (want !== isActive) return false
            }
            return true
        })
    }, [layouts, searchAndFilters])

    if (!canManage) {
        return (
            <Card>
                <MutedText>{i18n.formLayouts.noPermission}</MutedText>
            </Card>
        )
    }

    const hasAnyLayout = layouts.length > 0

    return (
        <PageLayout
            data-testid="form-layout-list-page"
            variant="list"
            title={i18n.formLayouts.title}
            description={i18n.formLayouts.subtitle}
            icon={CONCEPT_ICON.formLayout}
            count={layouts.length}
            loading={isLoading}
            error={error}
            filters={
                <UnifiedSearchBar
                    contract={FormLayoutControllerLayoutsEndpoint}
                    value={searchAndFilters}
                    onChange={setSearchAndFilters}
                />
            }
        >
                    {!hasAnyLayout && !isLoading && (
                        <InfoBanner variant="info" className="mb-4">
                            {i18n.formLayouts.emptyCallout}
                        </InfoBanner>
                    )}

                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead data-testid="col-use-case" className="w-full">{i18n.formLayouts.columns.useCase}</TableHead>
                                <TableHead data-testid="col-key" className="whitespace-nowrap w-48">{i18n.formLayouts.columns.key}</TableHead>
                                <TableHead data-testid="col-form-fields" className="whitespace-nowrap w-48">{i18n.formLayouts.columns.formFields}</TableHead>
                                <TableHead data-testid="col-status" className="whitespace-nowrap">{i18n.formLayouts.columns.status}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading
                                ? Array.from({length: 5}).map((_, i) => (
                                      <TableRow key={i}>
                                          <TableCell colSpan={4}>
                                              <Skeleton className="h-6 w-full" />
                                          </TableCell>
                                      </TableRow>
                                  ))
                                : visibleRows.map((row) => (
                                      <ClickableTableRow
                                          key={row.useCaseKey}
                                          to={routes.schema.formLayouts.detail(row.useCaseKey)}
                                      >
                                          <TableCell>
                                              <Strong>{row.displayName}</Strong>
                                              <MutedText size="xs" className="mt-0.5 block">
                                                  {i18n.formLayouts[row.wizardStepKey as keyof typeof i18n.formLayouts] as string}
                                              </MutedText>
                                          </TableCell>
                                          <TableCell className="whitespace-nowrap">
                                              <MonoText className="bg-muted">
                                                  {row.useCaseKey}
                                              </MonoText>
                                          </TableCell>
                                          <TableCell className="whitespace-nowrap">
                                              {row.layout ? (() => {
                                                  const total = row.layout.totalFieldCount
                                                  const used = Math.min(row.layout.fieldCount, total)
                                                  return (
                                                      <span>
                                                          <Strong>{used}</Strong>
                                                          {' '}
                                                          <MutedText>{i18n.formLayouts.fieldCountSuffix({total})}</MutedText>
                                                      </span>
                                                  )
                                              })() : i18n.common.emDash}
                                          </TableCell>
                                          <TableCell className="whitespace-nowrap">
                                              {row.layout ? (
                                                  row.layout.active ? (
                                                      <Badge variant="success" data-testid="badge-status-active">{i18n.formLayouts.statusActive}</Badge>
                                                  ) : (
                                                      <Badge variant="warning" data-testid="badge-status-inactive">{i18n.formLayouts.statusInactive}</Badge>
                                                  )
                                              ) : (
                                                  <Badge variant="outline" data-testid="badge-status-default">{i18n.formLayouts.statusDefault}</Badge>
                                              )}
                                          </TableCell>
                                      </ClickableTableRow>
                                  ))}
                        </TableBody>
                    </Table>
        </PageLayout>
    )
}
